import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight, X, Wrench } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import ItemMenu from "../../components/os/ItemMenu";
import OwnerFilter from "../../components/os/OwnerFilter";
import { useTeamFilter } from "../../hooks/useTeamFilter";
import { useI18n } from "../../context/I18nContext";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const STATUS_CLS = {
  activo: "bg-violet/10 text-violet",
  pausado: "bg-gold/15 text-gold",
  completado: "bg-emerald-500/10 text-emerald-600",
};

export default function Operaciones() {
  const tf = useTeamFilter();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", area: "", description: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [share, setShare] = useState(null);
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", area: "", description: "", status: "activo" });

  const load = () => api.get("/operations/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const { data: p } = await api.post("/operations/projects", form);
      setOpen(false); setForm({ name: "", area: "", description: "" });
      navigate(`/os/operaciones/${p.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const openEdit = (p) => {
    setEdit(p);
    setEditForm({ name: p.name, area: p.area || "", description: p.description || "", status: p.status });
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/operations/projects/${edit.id}`, editForm);
      setEdit(null); toast.success(t("os.ops.updated")); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || t("os.ops.err")); }
  };
  const doDelete = async (p) => {
    if (!window.confirm(t("os.ops.deleteConfirm")(p.name))) return;
    try {
      await api.delete(`/operations/projects/${p.id}`);
      toast.success(t("os.ops.deleted")); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || t("os.ops.err")); }
  };

  return (
    <OSLayout title={t("os.ops.title")}>
      {!data ? (
        <div className="text-navy/40">{t("os.ops.loading")}</div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
              <div className="overline text-navy/40 mb-2">{t("os.ops.subtitle")}</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.ops.title")}</h2>
              <p className="mt-2 text-navy/50 max-w-lg">{t("os.ops.intro")}</p>
            </motion.div>
            <div className="flex items-center gap-3">
              <OwnerFilter tf={tf} />
              <button data-testid="ops-new-project" onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
                <Plus className="w-4 h-4" /> {t("os.ops.newProject")}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {data.metrics.map((m, i) => (
              <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.06 }} data-testid={`ops-kpi-${m.key}`}
                className="bg-white border border-navy/10 rounded-lg p-6">
                <div className="overline text-navy/40">{m.label}</div>
                <div className="mt-3 text-3xl font-medium tracking-tight">{m.value}</div>
              </motion.div>
            ))}
          </div>

          {data.projects.length === 0 ? (
            <div className="text-navy/40 py-16 text-center border border-dashed border-navy/15 rounded-lg" data-testid="ops-empty">
              <Wrench className="w-6 h-6 mx-auto mb-3 text-navy/30" />
              {t("os.ops.emptyProjects")}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tf.filter(data.projects).map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: (i % 3) * 0.06 }} className="relative">
                  <div className="absolute top-4 right-4 z-10">
                    <ItemMenu testId={p.id} onInvite={() => setShare(p)} onEdit={() => openEdit(p)} onDelete={() => doDelete(p)} />
                  </div>
                  <Link to={`/os/operaciones/${p.id}`} data-testid={`ops-project-${p.id}`}
                    className="group block h-full bg-white border border-navy/10 rounded-lg p-6 hover:-translate-y-1 hover:border-navy/25 transition-all duration-500">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-medium tracking-tight">{p.name}</h3>
                        <div className="text-sm text-navy/50 mt-1">{p.area || t("os.ops.noArea")}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-navy/30 group-hover:text-violet transition-colors shrink-0 mr-8" />
                    </div>
                    <div className="mt-5 flex items-center justify-between">
                      <span className={`text-[10px] px-2 py-1 rounded-full ${STATUS_CLS[p.status]}`}>{t(`os.ops.status.${p.status}`)}</span>
                      <span className="text-sm text-navy/50">{t("os.ops.expenseLabel")}: ${Number(p.expense_total || 0).toLocaleString()}</span>
                    </div>
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-navy/40 mb-1.5"><span>{t("os.ops.stagesProgress")}</span><span>{p.progress}%</span></div>
                      <div className="h-1.5 rounded-full bg-navy/5 overflow-hidden">
                        <div className="h-full bg-gradient-violet" style={{ width: `${p.progress}%` }} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease }} onClick={(e) => e.stopPropagation()}
            data-testid="ops-create-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium">{t("os.ops.createTitle")}</h3>
              <button onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input required data-testid="ops-input-name" placeholder={t("os.ops.namePlaceholder")} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <input data-testid="ops-input-area" placeholder={t("os.ops.areaPlaceholder")} value={form.area}
                onChange={(e) => setForm({ ...form, area: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <textarea data-testid="ops-input-desc" placeholder={t("os.ops.descPlaceholder")} value={form.description} rows={3}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors resize-none" />
              <p className="text-xs text-navy/40">{t("os.ops.stagesHint")}</p>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <button data-testid="ops-create-submit" disabled={saving}
                className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-60">{t("os.ops.createSubmit")}</button>
            </form>
          </motion.div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setEdit(null)}>
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease }} onClick={(e) => e.stopPropagation()}
            data-testid="ops-edit-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-medium">{t("os.ops.editTitle")}</h3>
              <button onClick={() => setEdit(null)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-4">
              <input required data-testid="ops-edit-name" value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <input data-testid="ops-edit-area" placeholder={t("os.ops.areaPlaceholderShort")} value={editForm.area}
                onChange={(e) => setEditForm({ ...editForm, area: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <textarea data-testid="ops-edit-desc" placeholder={t("os.ops.descPlaceholderShort")} value={editForm.description} rows={3}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors resize-none" />
              <select data-testid="ops-edit-status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors">
                <option value="activo">{t("os.ops.status.activo")}</option><option value="pausado">{t("os.ops.status.pausado")}</option><option value="completado">{t("os.ops.status.completado")}</option>
              </select>
              <button data-testid="ops-edit-submit"
                className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.ops.saveChanges")}</button>
            </form>
          </motion.div>
        </div>
      )}

      {share && <ShareDialog collection="ops_projects" id={share.id} title={share.name} onClose={() => setShare(null)} />}
    </OSLayout>
  );
}
